export enum TagType {
  XPath = 'xpath',
  Condition = 'condition',
  Repeat = 'repeat',
}

/**
 * Represents the OpenDoPE attribute parts (e.g. <od:xpath id="x2">).
 * Also used in the ContentControl tag (e.g. od:xpath=x1 or od:repeat=x2).
 */
export class Tag {
  /**
   * Get the id part of the tag (e.g. x2, or /kpImageFields/image_1)
   */
  get id(): string {
    return this._id;
  }

  /**
   * Get the tag type (e.g. xpath, repeat, image)
   */
  get type(): string {
    return this._type;
  }

  /**
   * Get the qualified name (e.g. od)
   */
  get qname(): string {
    return this._qname;
  }

  /**
   * Get the content type; empty string otherwise.
   */
  get contentType(): string | undefined {
    return this._contentType;
  }

  /**
   * Set the content type eg: 'application/xhtml+xml'
   */
  set contentType(contentType: string) {
    this._contentType = contentType;
  }

  /**
   * Whether or not this is a KyloePartners special
   */
  get isKp(): boolean {
    return this._qname === 'kp';
  }
  // assume ContentType is always "od"
  private static regex = /(?<qname>od|kp):(?<type>xpath|repeat|condition|id|image)=(?<id>[0-9\/\w]+)(?:&od:ContentType=)?(?<ct>.*)/;

  private readonly _qname: string;
  private readonly _id: string;
  private readonly _type: string;
  private _contentType: string | undefined;
  constructor(id: string, type: string, qname: string, contentType?: string) {
    this._id = id;
    this._type = type;
    this._qname = qname;
    this._contentType = contentType;
  }

  /**
   * Parses the tag as it is defined in the ContentControl tag (e.g. od:xpath=x2).
   *
   * NOTE: will not work with signature fields due to <signature-provider-name>.<field-type> format.
   * @param tag the tag string to parse
   */
  static fromString(tag: string): Tag {
    const match = tag.match(Tag.regex); // 0: od:xpath=2, 1: xpath, 2: x2
    if (!match || !match.groups) {
      throw new Error(`Cannot parse |${tag}|`);
    }
    return new Tag(match.groups['id'], match.groups['type'], match.groups['qname'], match.groups['ct']); // es2018 - is it ok?
  }

  /**
   * Converts a Tag object back to string format
   */
  toString(): string {
    let tagStr = `${this._qname}:${this._type}=${this._id}`;
    if (this._contentType) {
      tagStr = `${tagStr}&od:ContentType=${this._contentType}`;
    }
    return tagStr;
  }
}
